import { Component, HostListener, inject, signal, computed, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { QuillEditorComponent } from 'ngx-quill';
import { ContentService, ContentResponse, EditContentRequest, CreateContentRequest, ListContentsResult } from '../../core/services/content';
import { ApplicationService, RegisteredAppResponse } from '../../core/services/application';
import { ImageGenerateResponse, ImageGenerationService } from '../../core/services/image-generation';
import { ToastService } from '../../core/services/toast';
import { SUPPORTED_CONTENT_LANGUAGES, contentLanguageLabel } from '../../core/constants/supported-languages';

export interface ContentGroup {
  id: string; // unique row id
  isGroup: boolean;
  model: string;
  app_id: string;
  created_at: string;
  is_approved: boolean;
  concept_id?: string | null;
  translation_group_id?: string | null;
  title: string;
  image_path?: string | null;
  tags: string[];
  items: ContentResponse[]; // all languages in this group
}

const PAGE_SIZE = 104;

type ContentFilterMenu = 'app' | 'approval' | 'language' | 'sort';

@Component({
  selector: 'app-content-manager',
  imports: [FormsModule, ReactiveFormsModule, DatePipe, QuillEditorComponent],
  templateUrl: './content-manager.html',
  styleUrl: './content-manager.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentManagerComponent implements OnInit, OnDestroy {
  private contentService = inject(ContentService);
  private appService = inject(ApplicationService);
  private imagesApi = inject(ImageGenerationService);
  private toast = inject(ToastService);
  private fb = inject(FormBuilder);

  // ── Data ──────────────────────────────────────────────────────────────────
  contents = signal<ContentResponse[]>([]);
  selectedIds = signal<Set<string>>(new Set());
  selectedCount = computed(() => this.selectedIds().size);
  groupedContents = computed<ContentGroup[]>(() => {
    const list = this.contents();
    const groups: ContentGroup[] = [];
    const groupMap = new Map<string, ContentGroup>();

    for (const c of list) {
      if (c.translation_group_id) {
        const key = `trgroup_${c.translation_group_id}`;
        let g = groupMap.get(key);
        if (!g) {
          g = {
            id: key,
            isGroup: true,
            model: c.model,
            app_id: c.app_id,
            created_at: c.created_at,
            is_approved: c.is_approved,
            concept_id: c.concept_id,
            translation_group_id: c.translation_group_id,
            title: c.title,
            image_path: c.image_path,
            tags: c.tags,
            items: []
          };
          groupMap.set(key, g);
          groups.push(g);
        }
        g.items.push(c);
        g.is_approved = g.items.every(item => item.is_approved);
        continue;
      }

      if (c.model === 'human' || !c.concept_id) {
        groups.push({
          id: c.content_id,
          isGroup: false,
          model: c.model,
          app_id: c.app_id,
          created_at: c.created_at,
          is_approved: c.is_approved,
          title: c.title,
          image_path: c.image_path,
          tags: c.tags,
          items: [c],
        });
      } else {
        const key = `${c.concept_id}_${c.image_path || 'noimg'}`;
        let g = groupMap.get(key);
        if (!g) {
          g = {
            id: key,
            isGroup: true,
            model: c.model,
            app_id: c.app_id,
            created_at: c.created_at,
            is_approved: c.is_approved, // will be updated below
            concept_id: c.concept_id,
            title: c.title,
            image_path: c.image_path,
            tags: c.tags,
            items: []
          };
          groupMap.set(key, g);
          groups.push(g);
        }
        g.items.push(c);
        g.is_approved = g.items.every(item => item.is_approved);
      }
    }
    // Update each group's title to the preferred language (tr > en > first)
    return groups.map(g => {
      if (g.items.length <= 1) return g;
      const idx = this.preferredItemIndex(g.items);
      return { ...g, title: g.items[idx].title };
    });
  });
  apps = signal<RegisteredAppResponse[]>([]);
  isLoading = signal(false);
  appsLoading = signal(false);
  errorMsg = signal<string | null>(null);

  // ── Filters ───────────────────────────────────────────────────────────────
  filterAppId = signal('');
  filterLanguage = signal('');
  filterApproved = signal('');
  filterSortOrder = signal<'newest_first' | 'oldest_first'>('newest_first');
  filterSearch = signal('');
  readonly filterMenus: Record<ContentFilterMenu, { open: ReturnType<typeof signal<boolean>>; closing: ReturnType<typeof signal<boolean>>; timer: ReturnType<typeof setTimeout> | null }> = {
    app: { open: signal(false), closing: signal(false), timer: null },
    approval: { open: signal(false), closing: signal(false), timer: null },
    language: { open: signal(false), closing: signal(false), timer: null },
    sort: { open: signal(false), closing: signal(false), timer: null },
  };
  createClosing = signal(false);
  editClosing = signal(false);
  deleteClosing = signal(false);
  private createCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private editCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private deleteCloseTimer: ReturnType<typeof setTimeout> | null = null;
  readonly languages = SUPPORTED_CONTENT_LANGUAGES;

  languageLabel(code: string): string {
    return contentLanguageLabel(code);
  }

  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  // ── Pagination ────────────────────────────────────────────────────────────
  page = signal(0);
  readonly pageSize = PAGE_SIZE;
  /** Count of items in the raw paginated page (before sibling expansion). */
  baseCount = signal(0);

  totalLoaded = computed(() => this.contents().length);
  hasPrev = computed(() => this.page() > 0);
  // A full page was returned → there may be more pages. Uses baseCount (pre-sibling-expansion)
  // so that extra sibling items don't falsely suppress the "Next" button.
  hasNext = computed(() => this.baseCount() === PAGE_SIZE);

  // ── Create modal state ────────────────────────────────────────────────────
  showCreateModal = signal(false);
  isCreating = signal(false);
  createError = signal<string | null>(null);
  createTags = signal<string[]>([]);
  createTagInput = signal('');
  createTargetLanguages = signal<string[]>([]);


  createForm = this.fb.group({
    app_id: ['', Validators.required],
    language: ['', Validators.required],
    title: ['', Validators.required],
    content: ['', Validators.required],
    image_path: [''],
  });

  // ── Modal state ───────────────────────────────────────────────────────────
  selectedGroup = signal<ContentGroup | null>(null);
  selectedEditTabIndex = signal<number>(0);

  activeEditItem = computed(() => {
    const g = this.selectedGroup();
    if (!g || !g.items.length) return null;
    return g.items[this.selectedEditTabIndex()] || g.items[0];
  });

  showEditModal = signal(false);
  showDeleteModal = signal(false);
  isSaving = signal(false);
  saveError = signal<string | null>(null);

  // Tags for edit modal
  editTags = signal<string[]>([]);
  editTagInput = signal('');
  editSharedImagePath = signal('');
  libraryImages = signal<ImageGenerateResponse[]>([]);
  libraryLoading = signal(false);
  libraryError = signal<string | null>(null);
  libraryTarget = signal<'create' | 'edit' | null>(null);

  editForm = this.fb.group({
    title: ['', Validators.required],
    content: ['', Validators.required],
  });

  /** Quill editor toolbar config */
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, false] }],
      ['link', 'blockquote'],
      ['clean'],
    ],
  };

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadApps();
    this.loadContents();
    this.searchInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      this.filterSearch.set(value);
      this.applyFilters();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    for (const menu of Object.values(this.filterMenus)) this.clearTimer(menu.timer);
    this.clearTimer(this.createCloseTimer);
    this.clearTimer(this.editCloseTimer);
    this.clearTimer(this.deleteCloseTimer);
  }

  @HostListener('document:click')
  closeFiltersFromOutside(): void {
    this.closeFilterMenus();
  }

  @HostListener('document:keydown.escape')
  closeFiltersFromEscape(): void {
    this.closeFilterMenus();
  }

  onSearchInput(value: string): void {
    this.searchInput$.next(value);
  }

  loadApps(): void {
    this.appsLoading.set(true);
    this.appService.getApps(0, 100).subscribe({
      next: list => { this.apps.set(list); this.appsLoading.set(false); },
      error: () => this.appsLoading.set(false),
    });
  }


  loadContents(): void {
    this.selectedIds.set(new Set());
    this.isLoading.set(true);
    this.errorMsg.set(null);
    this.contentService.list({
      app_id: this.filterAppId() || null,
      language: this.filterLanguage() || null,
      is_approved: this.filterApproved() === '' ? null : this.filterApproved() === 'true',
      search: this.filterSearch() || null,
      sort_order: this.filterSortOrder(),
      skip: this.page() * PAGE_SIZE,
      limit: PAGE_SIZE,
    }).subscribe({
      next: ({ items, baseCount }: ListContentsResult) => {
        this.contents.set(items);
        this.baseCount.set(baseCount);
        this.isLoading.set(false);
      },
      error: err => { this.errorMsg.set(err?.error?.detail ?? 'Failed to load contents.'); this.isLoading.set(false); },
    });
  }

  appFilterLabel(): string {
    return this.filterAppId() ? this.getAppName(this.filterAppId()) : 'All Apps';
  }

  approvalFilterLabel(): string {
    if (this.filterApproved() === 'true') return 'Approved';
    if (this.filterApproved() === 'false') return 'Not Approved';
    return 'All';
  }

  languageFilterLabel(): string {
    return this.filterLanguage() ? this.languageLabel(this.filterLanguage()) : 'All Languages';
  }

  sortFilterLabel(): string {
    return this.filterSortOrder() === 'oldest_first' ? 'Oldest first' : 'Newest first';
  }

  toggleFilterMenu(id: ContentFilterMenu): void {
    const menu = this.filterMenus[id];
    if (menu.open()) {
      this.closeFilterMenu(id);
      return;
    }
    (Object.keys(this.filterMenus) as ContentFilterMenu[])
      .filter(other => other !== id)
      .forEach(other => this.closeFilterMenu(other));
    this.clearTimer(menu.timer);
    menu.timer = null;
    menu.closing.set(false);
    menu.open.set(true);
  }

  chooseAppFilter(appId: string): void {
    if (appId !== this.filterAppId()) {
      this.filterAppId.set(appId);
      this.applyFilters();
    }
    this.closeFilterMenu('app');
  }

  chooseApprovalFilter(value: '' | 'true' | 'false'): void {
    if (value !== this.filterApproved()) {
      this.filterApproved.set(value);
      this.applyFilters();
    }
    this.closeFilterMenu('approval');
  }

  chooseLanguageFilter(code: string): void {
    if (code !== this.filterLanguage()) {
      this.filterLanguage.set(code);
      this.applyFilters();
    }
    this.closeFilterMenu('language');
  }

  chooseSortFilter(order: 'newest_first' | 'oldest_first'): void {
    if (order !== this.filterSortOrder()) {
      this.filterSortOrder.set(order);
      this.applyFilters();
    }
    this.closeFilterMenu('sort');
  }

  closeFilterMenus(): void {
    (Object.keys(this.filterMenus) as ContentFilterMenu[]).forEach(id => this.closeFilterMenu(id));
  }

  private closeFilterMenu(id: ContentFilterMenu): void {
    const menu = this.filterMenus[id];
    if (!menu.open() || menu.closing()) return;
    menu.closing.set(true);
    this.clearTimer(menu.timer);
    menu.timer = setTimeout(() => {
      menu.open.set(false);
      menu.closing.set(false);
      menu.timer = null;
    }, 200);
  }

  private clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
    if (timer) clearTimeout(timer);
  }

  applyFilters(): void {
    this.page.set(0);
    this.loadContents();
  }

  prevPage(): void {
    if (this.hasPrev()) { this.page.update(p => p - 1); this.loadContents(); }
  }

  nextPage(): void {
    if (this.hasNext()) { this.page.update(p => p + 1); this.loadContents(); }
  }

  // ── Inline actions ────────────────────────────────────────────────────────
  toggleApprove(g: ContentGroup): void {
    const targetApproval = !g.is_approved;
    const tasks = g.items
      .filter(item => item.is_approved !== targetApproval)
      .map(item => this.contentService.approve(item.content_id, { is_approved: targetApproval }));
      
    if (tasks.length === 0) return;
    
    forkJoin(tasks).subscribe({
      next: (results) => {
         results.forEach(updated => this.replaceContent(updated));
      },
      error: err => this.errorMsg.set('Failed to update approval for some items.')
    });
  }

  private replaceContent(updated: ContentResponse): void {
    this.contents.update(list => list.map(c => c.content_id === updated.content_id ? updated : c));
  }

  // ── Edit modal ────────────────────────────────────────────────────────────
  openEditModal(g: ContentGroup): void {
    this.clearTimer(this.editCloseTimer);
    this.editCloseTimer = null;
    this.editClosing.set(false);
    this.selectedGroup.set(g);
    this.selectedEditTabIndex.set(this.preferredItemIndex(g.items));
    this.saveError.set(null);
    this.editSharedImagePath.set(g.image_path ?? '');
    this.loadActiveEditItemToForm();
    this.showEditModal.set(true);
  }

  setEditTab(index: number): void {
    this.selectedEditTabIndex.set(index);
    this.saveError.set(null);
    this.loadActiveEditItemToForm();
  }

  private loadActiveEditItemToForm(): void {
    const c = this.activeEditItem();
    if (!c) return;
    this.editTags.set([...c.tags]);
    this.editTagInput.set('');
    this.editForm.reset({ title: c.title, content: c.content });
  }

  closeEditModal(): void {
    if (!this.showEditModal() || this.editClosing()) return;
    this.editClosing.set(true);
    this.editCloseTimer = setTimeout(() => {
      this.showEditModal.set(false);
      this.editClosing.set(false);
      this.selectedGroup.set(null);
      this.libraryTarget.set(null);
      this.editCloseTimer = null;
    }, 200);
  }

  toggleLibrary(target: 'create' | 'edit'): void {
    if (this.libraryTarget() === target) {
      this.libraryTarget.set(null);
      return;
    }
    this.libraryTarget.set(target);
    if (this.libraryImages().length || this.libraryLoading()) return;
    this.libraryLoading.set(true);
    this.libraryError.set(null);
    this.imagesApi.history(60).pipe(takeUntil(this.destroy$)).subscribe({
      next: list => {
        this.libraryImages.set(list.filter(image => !!image.image_url));
        this.libraryLoading.set(false);
      },
      error: () => {
        this.libraryError.set('Could not load the image library.');
        this.libraryLoading.set(false);
      },
    });
  }

  pickLibraryImage(url: string): void {
    if (this.libraryTarget() === 'create') {
      this.createForm.patchValue({ image_path: url });
    } else {
      this.editSharedImagePath.set(url);
    }
    this.libraryTarget.set(null);
  }

  currentLibraryUrl(): string {
    if (this.libraryTarget() === 'create') {
      return this.createForm.get('image_path')?.value ?? '';
    }
    return this.editSharedImagePath();
  }

  addEditTag(value: string): void {
    const tag = value.trim();
    if (!tag || this.editTags().includes(tag)) return;
    this.editTags.update(t => [...t, tag]);
    this.editTagInput.set('');
  }

  removeEditTag(tag: string): void {
    this.editTags.update(t => t.filter(x => x !== tag));
  }

  onEditTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addEditTag(this.editTagInput());
    }
  }

  submitEdit(): void {
    const c = this.activeEditItem();
    if (!c || this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
    const v = this.editForm.value;
    const cleanedContent = this.normalizeRichTextContent(v.content ?? '');
    if (!cleanedContent) {
      this.editForm.get('content')?.setErrors({ required: true });
      this.editForm.get('content')?.markAsTouched();
      this.saveError.set('Content cannot be empty.');
      return;
    }
    const payload: EditContentRequest = {
      title: v.title!,
      content: cleanedContent,
      image_path: this.editSharedImagePath() || null,
      tags: this.editTags(),
    };
    this.isSaving.set(true);
    this.saveError.set(null);
    this.contentService.edit(c.content_id, payload).subscribe({
      next: updated => {
        const merged = updated;
        const group = this.selectedGroup();
        const sharedImagePath = updated.image_path;
        this.contents.update(list => list.map(item => {
          const belongsToGroup = group?.items.some(groupItem => groupItem.content_id === item.content_id);
          if (item.content_id === merged.content_id) return merged;
          return belongsToGroup ? { ...item, image_path: sharedImagePath, is_approved: false } : item;
        }));
        
        this.selectedGroup.update(g => {
          if (!g) return g;
          const idx = g.items.findIndex(x => x.content_id === merged.content_id);
          if (idx !== -1) {
            const newItems = [...g.items];
            newItems[idx] = merged;
            const imageSyncedItems = newItems.map(item => ({
              ...item,
              image_path: sharedImagePath,
              is_approved: false,
            }));
            return {
              ...g,
              image_path: sharedImagePath,
              items: imageSyncedItems,
              is_approved: imageSyncedItems.every(i => i.is_approved),
            };
          }
          return g;
        });
        
        this.isSaving.set(false);
        // Only saving active tab, so we don't close the modal immediately anymore
        // Actually, let's show a success feedback conceptually or just let user stay? 
        // User might want to edit other tabs, so let's keep it open but saveError.set("Saved")?
        // But previously it closed. Since we have tabs, closing might be annoying if they want to edit multiple.
        // Let's keep modal open, but users usually expect 'Save' to close. Let's add a visual cue.
      },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to save.'); },
    });
  }

  /** Toggle approval from inside the edit modal */
  approveInModal(): void {
    const c = this.activeEditItem();
    if (!c) return;
    this.contentService.approve(c.content_id, { is_approved: !c.is_approved }).subscribe({
      next: updated => {
        this.replaceContent(updated);
        
        this.selectedGroup.update(g => {
          if (!g) return g;
          const idx = g.items.findIndex(x => x.content_id === updated.content_id);
          if (idx !== -1) {
            const newItems = [...g.items];
            newItems[idx] = updated;
            return { ...g, items: newItems, is_approved: newItems.every(i => i.is_approved) };
          }
          return g;
        });
      },
      error: err => this.saveError.set(err?.error?.detail ?? 'Failed to update approval.'),
    });
  }

  // ── Delete modal ──────────────────────────────────────────────────────────
  openDeleteModal(g: ContentGroup): void {
    this.clearTimer(this.deleteCloseTimer);
    this.deleteCloseTimer = null;
    this.deleteClosing.set(false);
    this.selectedGroup.set(g);
    this.saveError.set(null);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    if (!this.showDeleteModal() || this.deleteClosing()) return;
    this.deleteClosing.set(true);
    this.deleteCloseTimer = setTimeout(() => {
      this.showDeleteModal.set(false);
      this.deleteClosing.set(false);
      this.selectedGroup.set(null);
      this.deleteCloseTimer = null;
    }, 200);
  }

  openBulkDeleteModal(): void {
    if (this.selectedCount() === 0) return;
    this.clearTimer(this.deleteCloseTimer);
    this.deleteCloseTimer = null;
    this.deleteClosing.set(false);
    this.selectedGroup.set(null);
    this.saveError.set(null);
    this.showDeleteModal.set(true);
  }

  toggleSelect(groupId: string): void {
    this.selectedIds.update(set => {
      const next = new Set(set);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  isSelected(groupId: string): boolean {
    return this.selectedIds().has(groupId);
  }

  allSelected(): boolean {
    const groups = this.groupedContents();
    return groups.length > 0 && groups.every(group => this.selectedIds().has(group.id));
  }

  toggleSelectAll(): void {
    const groups = this.groupedContents();
    if (this.allSelected()) {
      this.selectedIds.set(new Set());
      return;
    }
    this.selectedIds.set(new Set(groups.map(group => group.id)));
  }

  confirmDelete(): void {
    const group = this.selectedGroup();
    const ids = group
      ? group.items.map(item => item.content_id)
      : this.groupedContents()
          .filter(item => this.selectedIds().has(item.id))
          .flatMap(item => item.items.map(content => content.content_id));
    if (ids.length === 0 || this.isSaving()) return;
    this.isSaving.set(true);
    this.saveError.set(null);

    this.contentService.bulkDelete(ids).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeDeleteModal();
        this.loadContents();
      },
      error: () => {
        this.isSaving.set(false);
        this.saveError.set('Failed to delete content.');
      },
    });
  }

  // ── Create modal ──────────────────────────────────────────────────────────
  openCreateModal(): void {
    this.clearTimer(this.createCloseTimer);
    this.createCloseTimer = null;
    this.createClosing.set(false);
    this.createError.set(null);
    this.createTags.set([]);
    this.createTagInput.set('');
    this.createTargetLanguages.set([]);
    this.createForm.reset();
    this.showCreateModal.set(true);
  }

  closeCreateModal(): void {
    if (!this.showCreateModal() || this.createClosing()) return;
    this.createClosing.set(true);
    this.createCloseTimer = setTimeout(() => {
      this.showCreateModal.set(false);
      this.createClosing.set(false);
      this.libraryTarget.set(null);
      this.createCloseTimer = null;
    }, 200);
  }

  onCreateAppChange(appId: string): void {
    this.createForm.patchValue({ });
  }

  toggleCreateTargetLanguage(language: string, checked: boolean): void {
    this.createTargetLanguages.update((langs) => {
      if (checked) {
        return langs.includes(language) ? langs : [...langs, language];
      }
      return langs.filter((l) => l !== language);
    });
  }

  addCreateTag(value: string): void {
    const tag = value.trim();
    if (!tag || this.createTags().includes(tag)) return;
    this.createTags.update(t => [...t, tag]);
    this.createTagInput.set('');
  }

  removeCreateTag(tag: string): void {
    this.createTags.update(t => t.filter(x => x !== tag));
  }

  onCreateTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addCreateTag(this.createTagInput());
    }
  }

  submitCreate(): void {
    if (this.createForm.invalid) { this.createForm.markAllAsTouched(); return; }
    const v = this.createForm.value;
    const cleanedContent = this.normalizeRichTextContent(v.content ?? '');
    if (!cleanedContent) {
      this.createForm.get('content')?.setErrors({ required: true });
      this.createForm.get('content')?.markAsTouched();
      this.createError.set('Content cannot be empty.');
      return;
    }
    const targetLanguages = this.createTargetLanguages();
    const hasTranslations = targetLanguages.length > 0;
    const payload: CreateContentRequest = {
      concept_id: null,
      app_id: v.app_id!,
      language: v.language!,
      target_languages: targetLanguages,
      title: v.title!,
      content: cleanedContent,
      image_path: v.image_path || null,
      tags: this.createTags(),
      model: 'human',
    };
    this.createError.set(null);

    if (hasTranslations) {
      this.toast.show('Isleniyor: Istediginiz ceviriler ve metin hazirlaniyor.', 'info', 5000);
    } else {
      this.toast.show('Icerik olusturma islemi baslatildi.', 'info', 3500);
    }

    this.isCreating.set(true);
    this.contentService.create(payload).subscribe({
      next: () => {
        this.toast.show(
          hasTranslations
            ? 'Ceviri ve metin olusturma islemi basariyla tamamlandi.'
            : 'Icerik basariyla olusturuldu.',
          'success',
          4500
        );
        this.page.set(0);
        this.loadContents();
      },
      error: err => {
        const errorDetail = err?.error?.detail ?? 'Icerik olusturma basarisiz oldu.';
        this.createError.set(errorDetail);
        this.toast.show(
          hasTranslations
            ? `Ceviri ve metin olusturma basarisiz: ${errorDetail}`
            : `Icerik olusturma basarisiz: ${errorDetail}`,
          'error',
          6000
        );
      },
    });

    // Keep UX non-blocking for potentially long translation jobs.
    this.isCreating.set(false);
    this.closeCreateModal();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  getAppName(appId: string): string {
    return this.apps().find(a => a.app_id === appId)?.app_name ?? appId;
  }

  preferredItemIndex(items: ContentResponse[]): number {
    const trIdx = items.findIndex(i => i.language === 'tr');
    if (trIdx !== -1) return trIdx;
    const enIdx = items.findIndex(i => i.language === 'en');
    if (enIdx !== -1) return enIdx;
    return 0;
  }

  private normalizeRichTextContent(raw: string): string {
    const source = (raw ?? '').trim();
    if (!source) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'text/html');
    const body = doc.body;

    body.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote').forEach((el) => {
      if (el.querySelector('br')) return;
      if (!el.textContent?.trim()) el.remove();
    });

    const text = (body.innerText || body.textContent || '')
      .replace(/\u00A0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();

    return text;
  }
}
